// Extracted from shared/schema.ts to keep that file under its grandfathered
// line cap. Holds recurrence types + the general_task_photos table (completion
// proof) + the shared zod input schema reused by routes.ts.

import { pgTable, serial, integer, text, timestamp } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { generalTasks, organizations, users, apartments, cleaningJobs } from "./schema";

// recurrenceRule shape stored as jsonb on general_tasks.
//   { frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly',
//     daysOfWeek?: number[],   // 0=Sun … 6=Sat — used by weekly/biweekly
//     dayOfMonth?: number,     // 1-31 — used by monthly
//     timeOfDay?: string }     // 'HH:mm' (24h) — copied onto each occurrence
export type RecurrenceRule = {
  frequency: "daily" | "weekly" | "biweekly" | "monthly";
  daysOfWeek?: number[];
  dayOfMonth?: number;
  timeOfDay?: string;
};

// Shared zod schema for recurrence input — used by admin create + update.
export const recurrenceRuleInput = z.object({
  frequency: z.enum(["daily", "weekly", "biweekly", "monthly"]),
  daysOfWeek: z.array(z.number().int().min(0).max(6)).optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  timeOfDay: z.string().regex(/^\d{2}:\d{2}$/).optional(),
});

// Shared payload schemas for /api/admin/general-tasks (extracted from
// routes.ts to keep that file under its line cap).
const taskTypeEnum = z.enum(["checkin", "checkout", "unit_setup", "inspection", "laundry", "other"]);
const statusEnum = z.enum(["pending", "in_progress", "completed"]);
export const adminCreateGeneralTaskInput = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  assignedTo: z.number().nullable().optional(),
  scheduledAt: z.string().optional(),
  address: z.string().nullable().optional(),
  apartmentId: z.number().nullable().optional(),
  taskType: taskTypeEnum.optional(),
  status: statusEnum.optional(),
  billable: z.boolean().optional(),
  billingAmount: z.string().nullable().optional(),
  billedToUserId: z.number().nullable().optional(),
  cleanerPayoutAmount: z.string().nullable().optional(),
  secondAssigneeId: z.number().nullable().optional(),
  cleaningJobId: z.number().nullable().optional(),
  recurrenceRule: recurrenceRuleInput.nullable().optional(),
  recurrenceEndDate: z.string().nullable().optional(),
});
export const adminUpdateGeneralTaskInput = z.object({
  title: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  assignedTo: z.number().nullable().optional(),
  scheduledAt: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  apartmentId: z.number().nullable().optional(),
  taskType: taskTypeEnum.optional(),
  status: statusEnum.optional(),
  billable: z.boolean().optional(),
  billingAmount: z.string().nullable().optional(),
  billedToUserId: z.number().nullable().optional(),
  cleanerPayoutAmount: z.string().nullable().optional(),
  secondAssigneeId: z.number().nullable().optional(),
  recurrenceRule: recurrenceRuleInput.nullable().optional(),
  recurrenceEndDate: z.string().nullable().optional(),
  completionNotes: z.string().nullable().optional(),
});

// === GENERAL TASK PHOTOS (completion proof — migration 0046) ===
export const generalTaskPhotos = pgTable("general_task_photos", {
  id: serial("id").primaryKey(),
  generalTaskId: integer("general_task_id").references(() => generalTasks.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  url: text("url").notNull(),
  caption: text("caption"),
  uploadedByUserId: integer("uploaded_by_user_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertGeneralTaskPhotoSchema = createInsertSchema(generalTaskPhotos).omit({ id: true, createdAt: true });
export type GeneralTaskPhoto = typeof generalTaskPhotos.$inferSelect;
export type InsertGeneralTaskPhoto = z.infer<typeof insertGeneralTaskPhotoSchema>;

export const generalTaskPhotosRelations = relations(generalTaskPhotos, ({ one }) => ({
  generalTask: one(generalTasks, { fields: [generalTaskPhotos.generalTaskId], references: [generalTasks.id] }),
  organization: one(organizations, { fields: [generalTaskPhotos.organizationId], references: [organizations.id] }),
  uploadedBy: one(users, { fields: [generalTaskPhotos.uploadedByUserId], references: [users.id] }),
}));

export const generalTasksRelations = relations(generalTasks, ({ one, many }) => ({
  organization: one(organizations, { fields: [generalTasks.organizationId], references: [organizations.id] }),
  apartment: one(apartments, { fields: [generalTasks.apartmentId], references: [apartments.id] }),
  assignee: one(users, { fields: [generalTasks.assignedTo], references: [users.id], relationName: "generalTaskAssignee" }),
  billedTo: one(users, { fields: [generalTasks.billedToUserId], references: [users.id], relationName: "generalTaskBilledTo" }),
  cleaningJob: one(cleaningJobs, { fields: [generalTasks.cleaningJobId], references: [cleaningJobs.id] }),
  photos: many(generalTaskPhotos),
}));
